// AulaLeccionPage.js — Vista de una lección en el aula (docs/Lac/curso_contenido.png).
//
//   /aula/:documentId                    → la lección donde quedó el alumno
//   /aula/:documentId/:modulo/:leccion   → esa lección
//   /contenidos?modulo=&leccion=         → con `aula.en_contenidos`, el curso de
//                                           `aula.documento_contenidos` (LAAC)
//
// Pantalla completa, sin el header ni el pie del sitio (como el «modo enfoque» de
// LearnDash): barra superior con el logo, el avance, «Anterior Lección» y
// «Marcar como completado»; lateral con el curso y sus lecciones; y al centro el
// título, la ruta con el estado, el video y la navegación.
//
// El avance es el del modo mentor: el video guarda por dónde va y se da por visto
// al 90 %, y «Marcar como completado» (mentor/marcar-leccion.php) lo marca a mano.
// Colores y opciones: config_json.aula del proyecto.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { mentor2Service } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { Circulo, Check } from '../components/landing/sections/CursoContenidoSection';
import {
  leccionesDelCurso, avanceDelCurso, urlLeccion, configAula, urlCurso, urlArchivo,
} from '../components/aula/aulaCurso';

const ANCHO_LATERAL = 438;

const Pantalla = styled.div`
  --aula-primario: ${p => p.$primario};
  --aula-secundario: ${p => p.$secundario};
  min-height: 100vh;
  background: #fff;
  font-family: Arial, Helvetica, sans-serif;
  color: #333;
`;

/* ---------- Barra superior ---------- */
const Barra = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: stretch;
  height: 64px;
  background: #fff;
  border-bottom: 1px solid #e2e7ed;
`;

const BarraLogo = styled(Link)`
  flex: 0 0 ${ANCHO_LATERAL}px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-sizing: border-box;
  img { max-height: 44px; max-width: 160px; }
  @media (max-width: 991px) { flex: 0 0 auto; padding: 0 12px; img { max-height: 36px; max-width: 110px; } }
`;

const BarraAvanceWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 20px;
  min-width: 0;
  .texto { font-size: 15px; color: #6b7280; margin-bottom: 6px; white-space: nowrap; }
  .texto strong { font-family: 'Roboto', Arial, sans-serif; font-weight: 800; letter-spacing: .4px; color: var(--aula-secundario); margin-right: 6px; }
  .barra { height: 6px; max-width: 395px; background: #e2e7ed; border-radius: 6px; overflow: hidden; }
  .barra span { display: block; height: 100%; background: var(--aula-primario); transition: width .4s ease; }
  @media (max-width: 991px) { padding: 0 10px; .texto { font-size: 12px; } }
`;

const BarraAccion = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 32px;
  border: none;
  border-left: 1px solid #e2e7ed;
  background: #fff;
  color: var(--aula-primario);
  font-family: 'Roboto', Arial, sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: .3px;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  &:hover:not(:disabled) { background: #faf5ff; }
  &:disabled { color: #9ca3af; cursor: default; }
  ${p => p.$secundario && css`color: var(--aula-secundario);`}
  svg { width: 20px; height: 20px; }
  @media (max-width: 1200px) { padding: 0 16px; font-size: 15px; }
  @media (max-width: 991px) { display: none; }
`;

const Usuario = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 22px;
  border-left: 1px solid #e2e7ed;
  color: #6b7280;
  font-size: 16px;
  white-space: nowrap;
  .avatar { width: 48px; height: 48px; border-radius: 50%; background: #e5e7eb; overflow: hidden; display: flex; align-items: center; justify-content: center; }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }
  .avatar svg { width: 60%; height: 60%; color: #fff; }
  @media (max-width: 1200px) { .nombre { display: none; } }
  @media (max-width: 991px) { padding: 0 10px; .avatar { width: 36px; height: 36px; } }
  @media (max-width: 600px) { display: none; }
`;

const MenuMovil = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 64px;
  border: none;
  border-left: 1px solid #e2e7ed;
  background: #fff;
  color: var(--aula-primario);
  cursor: pointer;
  svg { width: 26px; height: 26px; }
  @media (max-width: 991px) { display: flex; }
`;

/* ---------- Cuerpo: lateral + contenido ---------- */
const Cuerpo = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Lateral = styled.aside`
  position: sticky;
  top: 64px;
  flex: 0 0 ${ANCHO_LATERAL}px;
  width: ${ANCHO_LATERAL}px;
  height: calc(100vh - 64px);
  overflow-y: auto;
  border-right: 1px solid #e2e7ed;
  background: #fff;
  transition: margin-left .3s ease;
  ${p => !p.$abierto && css`margin-left: -${ANCHO_LATERAL}px;`}
  @media (max-width: 991px) {
    position: fixed;
    left: 0;
    z-index: 30;
    width: min(${ANCHO_LATERAL}px, 88vw);
    flex-basis: auto;
    box-shadow: 0 20px 50px rgb(0 0 0 / .2);
    margin-left: 0;
    transform: translateX(${p => (p.$abierto ? '0' : '-105%')});
    transition: transform .3s ease;
  }
`;

const LateralCabecera = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 50px 22px 24px;
  background: var(--aula-primario);
  color: #fff;
  font-family: 'Roboto', Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  svg { flex: 0 0 22px; width: 22px; height: 22px; align-self: flex-start; margin-top: 5px; }
`;

const Plegar = styled.button`
  position: fixed;
  z-index: 25;
  top: ${64 + 30}px;
  left: ${p => (p.$abierto ? ANCHO_LATERAL - 18 : 8)}px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: var(--aula-primario);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: left .3s ease, transform .3s ease;
  box-shadow: 0 2px 8px rgb(0 0 0 / .15);
  svg { width: 18px; height: 18px; transform: rotate(${p => (p.$abierto ? 0 : 180)}deg); transition: transform .3s ease; }
  &:focus-visible { outline: 3px solid var(--aula-secundario); outline-offset: 2px; }
  @media (max-width: 991px) { display: none; }
`;

const ListaLecciones = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  .modulo { padding: 14px 24px; background: #f6f7f9; color: #6b7280; font-size: 14px; font-weight: 700; text-transform: none; border-bottom: 1px solid #e2e7ed; }
`;

const ItemLeccion = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 24px;
  border-bottom: 1px solid #e2e7ed;
  color: ${p => (p.$actual ? 'var(--aula-primario)' : '#444')};
  font-size: 20px;
  font-weight: ${p => (p.$actual ? 700 : 400)};
  line-height: 1.4;
  text-decoration: none;
  &:hover, &:focus-visible { background: #faf7ff; outline: none; }
  > span:first-child { margin-top: 5px; }
  @media (max-width: 991px) { font-size: 17px; }
`;

const Contenido = styled.main`
  flex: 1;
  min-width: 0;
  padding: 80px 5% 60px;
  box-sizing: border-box;
  @media (max-width: 991px) { padding: 30px 16px 50px; }
`;

const Columna = styled.div`
  max-width: 1136px;
  margin: 0 auto;
`;

const TituloLeccion = styled.h1`
  margin: 0 0 18px;
  padding: 0;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 38px;
  font-weight: 700;
  line-height: 1.2;
  color: ${p => p.$color};
  @media (max-width: 991px) { font-size: 26px; }
`;

const Ruta = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 22px;
  background: #eef1f4;
  border-radius: 6px;
  font-size: 16px;
  ol { list-style: none; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 0; padding: 0; }
  li + li::before { content: '>'; color: #6b7280; margin-right: 6px; }
  a { color: var(--aula-primario); font-weight: 700; text-decoration: none; }
  a:hover { text-decoration: underline; }
  .estado { padding: 5px 16px; border-radius: 999px; background: var(--aula-primario); color: #fff; font-family: 'Roboto', Arial, sans-serif; font-size: 13px; font-weight: 800; letter-spacing: .6px; white-space: nowrap; }
  .estado.hecho { background: var(--aula-secundario); }
`;

const Video = styled.div`
  position: relative;
  margin-top: 24px;
  padding-top: 56.25%;
  background: #eef1f4;
  iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
`;

const Materiales = styled.ul`
  list-style: none;
  margin: 22px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  a { display: inline-flex; gap: 8px; align-items: center; padding: 10px 16px; border: 2px solid #e2e7ed; border-radius: 6px; color: var(--aula-primario); font-weight: 700; text-decoration: none; }
  a:hover { border-color: var(--aula-primario); }
`;

const Acciones = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  gap: 16px;
  margin-top: 26px;
  padding-top: 30px;
  border-top: 2px solid #e2e7ed;
  .izq { justify-self: start; }
  .der { justify-self: end; }
  .centro { display: flex; flex-direction: column; align-items: center; gap: 14px; }
  .volver { color: var(--aula-secundario); font-size: 16px; text-decoration: underline; }
  @media (max-width: 767px) {
    grid-template-columns: 1fr 1fr;
    .centro { grid-column: 1 / -1; grid-row: 1; }
    .izq, .der { justify-self: stretch; }
  }
`;

const Boton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-width: 238px;
  min-height: 55px;
  padding: 10px 28px;
  border: none;
  border-radius: 30px;
  background: ${p => (p.$secundario ? 'var(--aula-secundario)' : 'var(--aula-primario)')};
  color: #fff;
  font-family: 'Roboto', Arial, sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: .3px;
  text-decoration: none;
  cursor: pointer;
  box-sizing: border-box;
  transition: opacity .2s ease, transform .2s ease;
  ${p => p.$secundario && css`border-radius: 18px; max-width: 238px;`}
  &:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
  &:disabled { opacity: .6; cursor: default; }
  svg { flex: 0 0 20px; width: 20px; height: 20px; }
  @media (max-width: 767px) { min-width: 0; width: 100%; font-size: 15px; padding: 10px 14px; }
`;

const Hecho = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border-radius: 18px;
  border: 2px solid var(--aula-secundario);
  color: var(--aula-secundario);
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 800;
  svg { width: 18px; height: 18px; }
`;

const Centro = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  text-align: center;
  padding: 20px;
`;

const Velo = styled.div`
  display: none;
  @media (max-width: 991px) {
    display: ${p => (p.$abierto ? 'block' : 'none')};
    position: fixed;
    inset: 64px 0 0 0;
    z-index: 29;
    background: rgb(0 0 0 / .35);
  }
`;

const Flecha = ({ izquierda }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={izquierda ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);

const IconoDocumento = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" />
  </svg>
);

const IconoPersona = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="8" r="4.2" /><path d="M3.5 21c.8-4.4 4.2-7 8.5-7s7.7 2.6 8.5 7z" />
  </svg>
);

const IconoMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Carga una vez la API del reproductor de Vimeo (la misma que usa el modo mentor).
let promesaVimeo = null;
const cargarVimeo = () => {
  if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);
  if (!promesaVimeo) {
    promesaVimeo = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = () => resolve(window.Vimeo);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }
  return promesaVimeo;
};

const AulaLeccionPage = () => {
  const params = useParams();
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { proyecto } = useProject();
  const aula = configAula(proyecto);
  const docId = Number(params.documentId || aula.documento_contenidos);
  const modulo = params.modulo || query.get('modulo');
  const leccion = params.leccion || query.get('leccion');

  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [marcando, setMarcando] = useState(false);
  const [lateralAbierto, setLateralAbierto] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 991 : true));
  const iframeRef = useRef(null);

  const cargar = useCallback(() => (
    mentor2Service.start(docId)
      .then(r => { setDatos(r.data?.data || null); setError(''); })
      .catch(err => setError(err.response?.data?.message || 'No se pudo cargar el curso.'))
  ), [docId]);

  useEffect(() => { cargar(); }, [cargar]);

  const lecciones = useMemo(() => leccionesDelCurso(datos), [datos]);
  const avance = avanceDelCurso(lecciones);
  const indice = lecciones.findIndex(l => l.modulo === Number(modulo) && l.numero === Number(leccion));
  const actual = indice >= 0 ? lecciones[indice] : null;
  const anterior = indice > 0 ? lecciones[indice - 1] : null;
  const siguiente = indice >= 0 && indice < lecciones.length - 1 ? lecciones[indice + 1] : null;
  const volver = urlCurso(proyecto, docId);
  const tituloCurso = datos?.estructura?.titulo_programa || datos?.document_title || '';

  // Al cambiar de lección en el móvil se cierra la lista y se sube al inicio.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.innerWidth <= 991) setLateralAbierto(false);
  }, [modulo, leccion]);

  useEffect(() => {
    if (actual) document.title = `${actual.titulo} — ${tituloCurso}`;
  }, [actual, tituloCurso]);

  // Avance del video: se guarda cada 10 s, al pausar y al terminar; al 90 % cuenta como vista.
  const video = actual?.video;
  useEffect(() => {
    if (!video?.video_id || !iframeRef.current) return undefined;
    let player = null;
    let maximo = video.timestamp_maximo || 0;
    let ultimoGuardado = 0;
    let marcadaVista = !!video.completado;
    let cancelado = false;

    const guardar = (segundos, completado = 0) => mentor2Service.saveVideoProgress({
      video_id: video.video_id,
      document_id: docId,
      timestamp_actual: Math.floor(segundos),
      timestamp_maximo: Math.floor(maximo),
      completado,
    }).catch(() => {});

    cargarVimeo().then((Vimeo) => {
      if (cancelado || !iframeRef.current) return;
      player = new Vimeo.Player(iframeRef.current);
      const reanudar = video.timestamp_actual || 0;
      if (reanudar > 5 && (!video.duracion_segundos || reanudar < video.duracion_segundos - 5)) {
        player.setCurrentTime(reanudar).catch(() => {});
      }
      player.on('timeupdate', ({ seconds, duration }) => {
        maximo = Math.max(maximo, seconds);
        const vista = duration > 0 && maximo >= duration * 0.9;
        if (vista && !marcadaVista) {
          marcadaVista = true;
          guardar(seconds, 1).then(cargar);
          return;
        }
        if (seconds - ultimoGuardado >= 10 || seconds < ultimoGuardado) {
          ultimoGuardado = seconds;
          guardar(seconds);
        }
      });
      player.on('pause', ({ seconds }) => guardar(seconds));
      player.on('ended', ({ seconds }) => {
        maximo = Math.max(maximo, seconds);
        if (!marcadaVista) { marcadaVista = true; guardar(seconds, 1).then(cargar); }
      });
    }).catch(() => {});

    return () => {
      cancelado = true;
      if (player) { player.off('timeupdate'); player.off('pause'); player.off('ended'); }
    };
  // El reproductor se engancha una vez por video; el avance que llega después no lo reinicia.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.video_id, docId]);

  const marcar = async () => {
    if (!actual || marcando) return;
    setMarcando(true);
    try {
      await mentor2Service.marcarLeccion(docId, actual.modulo, actual.numero);
      await cargar();
      if (siguiente) navigate(urlLeccion(docId, siguiente, proyecto));
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo marcar la lección.');
    } finally {
      setMarcando(false);
    }
  };

  // Sin lección en la dirección: a la lección donde quedó el alumno (o la primera).
  if (datos && lecciones.length && (!modulo || !leccion || !actual)) {
    const destino = lecciones.find(l => l.modulo === datos.modulo_actual && l.numero === datos.leccion_actual)
      || lecciones.find(l => !l.completado) || lecciones[0];
    return <Navigate to={urlLeccion(docId, destino, proyecto)} replace />;
  }

  const logo = proyecto?.logo ? urlArchivo(proyecto.logo) : null;
  const avatar = user?.avatar_url ? urlArchivo(user.avatar_url) : null;
  const nombre = (user?.nombre || '').split(' ')[0];
  const puedeMarcar = aula.permitirMarcar && actual && !actual.completado;
  const materiales = (actual?.items || []).filter(it => it.tipo !== 'video' && it.file_path);

  return (
    <Pantalla $primario={aula.primario} $secundario={aula.secundario}>
      <Barra>
        <BarraLogo to={volver} aria-label="Volver al curso">
          {logo ? <img src={logo} alt={proyecto?.nombre || ''} /> : <strong>{proyecto?.nombre}</strong>}
        </BarraLogo>
        <BarraAvanceWrap aria-label="Avance del curso">
          <div className="texto"><strong>{avance.porcentaje}% COMPLETADO</strong>{avance.hechas}/{avance.total} pasos</div>
          <div className="barra" aria-hidden="true"><span style={{ width: `${avance.porcentaje}%` }} /></div>
        </BarraAvanceWrap>
        {anterior && (
          <BarraAccion as={Link} to={urlLeccion(docId, anterior, proyecto)}>
            <Flecha izquierda /> {aula.texto_anterior || 'Anterior Lección'}
          </BarraAccion>
        )}
        {aula.permitirMarcar && actual && (
          <BarraAccion type="button" $secundario onClick={marcar} disabled={!puedeMarcar || marcando}>
            {actual.completado ? (aula.texto_completada || 'Completada') : (marcando ? 'Marcando…' : (aula.texto_marcar || 'Marcar como completado'))}
            <Check />
          </BarraAccion>
        )}
        <Usuario>
          <span className="nombre">Hola {nombre || 'de nuevo'}!</span>
          <span className="avatar">{avatar ? <img src={avatar} alt="" /> : <IconoPersona />}</span>
        </Usuario>
        <MenuMovil type="button" aria-label="Lecciones del curso" aria-expanded={lateralAbierto} onClick={() => setLateralAbierto(x => !x)}>
          <IconoMenu />
        </MenuMovil>
      </Barra>

      <Cuerpo>
        <Lateral $abierto={lateralAbierto} aria-label="Lecciones del curso">
          <LateralCabecera><IconoDocumento /><span>{tituloCurso}</span></LateralCabecera>
          <ListaLecciones>
            {lecciones.map((l, i) => (
              <React.Fragment key={`${l.modulo}-${l.numero}`}>
                {(datos?.estructura?.modulos?.length || 0) > 1 && (i === 0 || lecciones[i - 1].modulo !== l.modulo) && (
                  <li className="modulo">{l.moduloTitulo}</li>
                )}
                <li>
                  <ItemLeccion to={urlLeccion(docId, l, proyecto)} $actual={i === indice} aria-current={i === indice ? 'page' : undefined}>
                    <Circulo $size={20} $hecho={l.completado} $actual={i === indice} $color={i === indice && !l.completado ? aula.primario : aula.secundario}>
                      {l.completado && <Check />}
                    </Circulo>
                    <span>{l.titulo}</span>
                  </ItemLeccion>
                </li>
              </React.Fragment>
            ))}
          </ListaLecciones>
        </Lateral>
        <Plegar type="button" $abierto={lateralAbierto} onClick={() => setLateralAbierto(x => !x)}
          aria-label={lateralAbierto ? 'Ocultar las lecciones' : 'Mostrar las lecciones'}>
          <Flecha izquierda />
        </Plegar>
        <Velo $abierto={lateralAbierto} onClick={() => setLateralAbierto(false)} />

        <Contenido>
          {!datos && !error && <Centro>Cargando la lección…</Centro>}
          {error && !actual && <Centro>{error}</Centro>}
          {actual && (
            <Columna>
              <TituloLeccion $color={aula.titulo}>{actual.titulo}</TituloLeccion>
              <Ruta aria-label="Ruta">
                <ol>
                  <li><Link to={volver}>{tituloCurso}</Link></li>
                  <li><Link to={urlLeccion(docId, actual, proyecto)} aria-current="page">{actual.titulo}</Link></li>
                </ol>
                <span className={`estado${actual.completado ? ' hecho' : ''}`}>
                  {actual.completado ? (aula.texto_estado_completo || 'COMPLETO') : (aula.texto_estado_progreso || 'EN PROGRESO')}
                </span>
              </Ruta>

              {video?.vimeo_id ? (
                <Video key={video.video_id}>
                  <iframe
                    ref={iframeRef}
                    title={actual.titulo}
                    src={`https://player.vimeo.com/video/${video.vimeo_id}?h=${video.hash_privacidad || ''}&dnt=1&title=0&byline=0&portrait=0`}
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    allowFullScreen
                  />
                </Video>
              ) : null}

              {materiales.length > 0 && (
                <Materiales>
                  {materiales.map(m => (
                    <li key={m.item_id}>
                      <a href={urlArchivo(m.file_path)} target="_blank" rel="noopener noreferrer">📄 {m.titulo || m.original_name}</a>
                    </li>
                  ))}
                </Materiales>
              )}

              {error && <p role="alert" style={{ color: '#b91c1c', marginTop: 16 }}>{error}</p>}

              <Acciones>
                <div className="izq">
                  {anterior && (
                    <Boton as={Link} to={urlLeccion(docId, anterior, proyecto)}>
                      <Flecha izquierda /> {aula.texto_anterior || 'Anterior Lección'}
                    </Boton>
                  )}
                </div>
                <div className="centro">
                  {aula.permitirMarcar && !actual.completado && (
                    <Boton type="button" $secundario onClick={marcar} disabled={marcando}>
                      {marcando ? 'Marcando…' : (aula.texto_marcar || 'Marcar como completado')} <Check />
                    </Boton>
                  )}
                  {actual.completado && <Hecho><Check /> {aula.texto_completada || 'Lección completada'}</Hecho>}
                  <Link className="volver" to={volver}>{aula.texto_volver || 'Volver al Curso'}</Link>
                </div>
                <div className="der">
                  {siguiente && (
                    <Boton as={Link} to={urlLeccion(docId, siguiente, proyecto)}>
                      {aula.texto_siguiente || 'Siguiente Lección'} <Flecha />
                    </Boton>
                  )}
                </div>
              </Acciones>
            </Columna>
          )}
        </Contenido>
      </Cuerpo>
    </Pantalla>
  );
};

export default AulaLeccionPage;
